import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { type CreateTemplateInput, createTemplateInputSchema, type Fuel, type Origin, type TemplateDetail } from 'used-vehicle-contracts';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import { CatalogSelect } from '../../components/ui/CatalogSelect';
import { FormField } from '../../components/ui/FormField';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Sheet } from '../../components/ui/Sheet';
import { Textarea } from '../../components/ui/Textarea';
import { clearableTextFieldOptions, numberFieldOptions, serverMessage, submitErrorKey } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { makesOf, modelsOf, TRIMS, trimOptions } from '../../lib/vehicle-catalog';

const FUELS: readonly Fuel[] = ['GAS', 'HYBRID', 'EV', 'DIESEL'];
const ORIGINS: readonly Origin[] = ['AGENCY', 'GCC', 'IMPORTED', 'OTHER'];

export interface TemplateSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Absent for a new template; present to edit the identity of an existing one. */
  template?: TemplateDetail | null;
  onSaved?: (template: TemplateDetail) => void;
}

/**
 * The template identity: which vehicles this template applies to (plan §3.2, PP §6B).
 *
 * Creating one also creates its first draft version on the server, pre-filled from the starter preset —
 * which is why the preset is chosen here and the *values* are edited on their own page.
 */
export const TemplateSheet = ({ isOpen, onClose, template = null, onSaved }: TemplateSheetProps) => {
  const { t } = useI18nTranslation();
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const presetsQuery = useQuery(orpc.templates.depreciationPresets.queryOptions({ enabled: isOpen }));
  // The makes and models the workspace already prices, for the two drop-downs (plan §11.19).
  const catalogQuery = useQuery(
    orpc.templates.list.queryOptions({
      input: { page: 1, pageSize: 100, sortBy: 'name', sortDirection: 'asc', includeArchived: true },
      enabled: isOpen,
    }),
  );
  const catalogTemplates = useMemo(() => catalogQuery.data?.items ?? [], [catalogQuery.data]);

  const isEditing = template !== null;

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm<CreateTemplateInput>({
    resolver: zodResolver(createTemplateInputSchema),
    defaultValues: { name: '', make: 'Toyota', model: '', fuel: 'HYBRID', depreciationPreset: 'MARKAZIA_NORMAL' },
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    reset(
      template
        ? {
            name: template.name,
            make: template.make,
            model: template.model,
            generation: template.generation ?? undefined,
            trim: template.trim ?? undefined,
            modelYearFrom: template.modelYearFrom ?? undefined,
            modelYearTo: template.modelYearTo ?? undefined,
            fuel: template.fuel,
            origin: template.origin ?? undefined,
            depreciationPreset: template.depreciationPreset ?? undefined,
            description: template.description ?? undefined,
          }
        : { name: '', make: 'Toyota', model: '', fuel: 'HYBRID', depreciationPreset: 'MARKAZIA_NORMAL' },
    );
  }, [isOpen, reset, template]);

  const invalidate = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: orpc.templates.list.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.templates.get.key() }),
    ]);
  };

  const onError = (error: unknown): void => setSubmitError(serverMessage(error) ?? t(submitErrorKey(error)));

  const createTemplate = useMutation(
    orpc.templates.create.mutationOptions({
      onSuccess: async (saved) => {
        await invalidate();
        reset();
        onSaved?.(saved);
        onClose();
      },
      onError,
    }),
  );

  const updateTemplate = useMutation(
    orpc.templates.update.mutationOptions({
      onSuccess: async (saved) => {
        await invalidate();
        onSaved?.(saved);
        onClose();
      },
      onError,
    }),
  );

  const isPending = createTemplate.isPending || updateTemplate.isPending;

  const chosenMake = watch('make') ?? '';
  const chosenTrim = watch('trim') ?? '';

  const close = (): void => {
    setSubmitError(null);
    onClose();
  };

  const onSubmit = handleSubmit((values) => {
    setSubmitError(null);

    if (template) {
      updateTemplate.mutate({ ...values, templateId: template.id });

      return;
    }

    createTemplate.mutate(values);
  });

  return (
    <Sheet
      isOpen={isOpen}
      onClose={close}
      title={isEditing ? t('templates.editTitle') : t('templates.createTitle')}
      description={isEditing ? t('templates.editSubtitle') : t('templates.createSubtitle')}
      footer={
        <>
          <Button
            variant="outline"
            onClick={close}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            type="submit"
            form="template-identity-form"
            isLoading={isPending}
          >
            {isPending ? t('actions.saving') : isEditing ? t('actions.save') : t('actions.create')}
          </Button>
        </>
      }
    >
      <form
        id="template-identity-form"
        onSubmit={onSubmit}
        noValidate
        className="space-y-4"
      >
        <FormField
          id="template-name"
          label={t('templates.name')}
          hint={t('templates.nameHint')}
          error={errors.name?.message}
          required
        >
          <Input
            id="template-name"
            autoFocus
            hasError={Boolean(errors.name)}
            {...register('name')}
          />
        </FormField>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            id="template-make"
            label={t('templates.make')}
            hint={t('templates.makeHint')}
            error={errors.make?.message}
            required
          >
            <Controller
              control={control}
              name="make"
              render={({ field }) => (
                <CatalogSelect
                  id="template-make"
                  value={field.value ?? ''}
                  options={makesOf(catalogTemplates)}
                  placeholder={t('catalog.chooseMake')}
                  otherLabel={t('catalog.otherMake')}
                  otherPlaceholder={t('catalog.typeMake')}
                  hasError={Boolean(errors.make)}
                  onChange={field.onChange}
                />
              )}
            />
          </FormField>

          <FormField
            id="template-model"
            label={t('templates.model')}
            error={errors.model?.message}
            required
          >
            <Controller
              control={control}
              name="model"
              render={({ field }) => (
                <CatalogSelect
                  id="template-model"
                  value={field.value ?? ''}
                  options={modelsOf(catalogTemplates, chosenMake)}
                  placeholder={t('catalog.chooseModel')}
                  otherLabel={t('catalog.otherModel')}
                  otherPlaceholder={t('catalog.typeModel')}
                  hasError={Boolean(errors.model)}
                  onChange={field.onChange}
                />
              )}
            />
          </FormField>

          <FormField
            id="template-generation"
            label={t('templates.generation')}
            hint={t('common.optional')}
          >
            <Input
              id="template-generation"
              {...register('generation', clearableTextFieldOptions)}
            />
          </FormField>

          <FormField
            id="template-trim"
            label={t('templates.trim')}
            hint={t('common.optional')}
          >
            <Select
              id="template-trim"
              placeholder={t('catalog.chooseTrim')}
              options={trimOptions(chosenTrim).map((trim) => ({
                value: trim,
                label: (TRIMS as readonly string[]).includes(trim) ? t(`enums.trim.${trim}`) : trim,
              }))}
              {...register('trim', clearableTextFieldOptions)}
            />
          </FormField>

          <FormField
            id="template-year-from"
            label={t('templates.modelYearFrom')}
            hint={t('common.optional')}
            error={errors.modelYearFrom?.message}
          >
            <Input
              id="template-year-from"
              type="number"
              dir="ltr"
              className="tabular-nums"
              {...register('modelYearFrom', numberFieldOptions)}
            />
          </FormField>

          <FormField
            id="template-year-to"
            label={t('templates.modelYearTo')}
            hint={t('common.optional')}
            error={errors.modelYearTo?.message}
          >
            <Input
              id="template-year-to"
              type="number"
              dir="ltr"
              className="tabular-nums"
              {...register('modelYearTo', numberFieldOptions)}
            />
          </FormField>

          <FormField
            id="template-fuel"
            label={t('templates.fuel')}
            error={errors.fuel?.message}
            required
          >
            <Select
              id="template-fuel"
              options={FUELS.map((fuel) => ({ value: fuel, label: t(`enums.fuel.${fuel}`) }))}
              hasError={Boolean(errors.fuel)}
              {...register('fuel')}
            />
          </FormField>

          <FormField
            id="template-origin"
            label={t('templates.origin')}
            hint={t('common.optional')}
          >
            <Select
              id="template-origin"
              placeholder={t('common.notSet')}
              options={ORIGINS.map((origin) => ({ value: origin, label: t(`enums.origin.${origin}`) }))}
              {...register('origin', clearableTextFieldOptions)}
            />
          </FormField>
        </div>

        <FormField
          id="template-preset"
          label={t('templates.preset')}
          hint={isEditing ? t('templates.presetHintEditing') : t('templates.presetHint')}
        >
          <Select
            id="template-preset"
            disabled={isEditing}
            placeholder={t('templates.presetNone')}
            options={(presetsQuery.data ?? []).map((preset) => ({ value: preset.key, label: preset.label }))}
            {...register('depreciationPreset', clearableTextFieldOptions)}
          />
        </FormField>

        <FormField
          id="template-description"
          label={t('templates.description')}
          hint={t('common.optional')}
        >
          <Textarea
            id="template-description"
            rows={3}
            {...register('description', clearableTextFieldOptions)}
          />
        </FormField>

        {submitError ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {submitError}
          </p>
        ) : null}
      </form>
    </Sheet>
  );
};
